// src/pages/Home.jsx
import React, { useState, useEffect } from "react";
import logo from "../images/logo.png";
import google from "../images/google.png";
import { supabase } from "../shared/supabase";

export default function Home() {
    const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
    const [rating, setRating] = useState(5);
    const [charCount, setCharCount] = useState(0);
    const [error, setError] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => setIsVisible(true), []);

    useEffect(() => {
        if (showPopup) {
            const timer = setTimeout(() => setShowPopup(false), 3000); // popup disappears after 3s
            return () => clearTimeout(timer);
        }
    }, [showPopup]);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({ ...prev, [id]: value }));
        if (id === "message") setCharCount(value.length);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        const { name, email, phone, message } = formData;
        const emailRegex = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/;
        const phoneRegex = /^[0-9]{10,15}$/;

        if (!name || !email || !message || !phone) {
            setError("Please fill in all required fields.");
            setIsLoading(false);
            return;
        }
        if (!emailRegex.test(email)) {
            setError("Please enter a valid email address.");
            setIsLoading(false);
            return;
        }
        if (phone && !phoneRegex.test(phone)) {
            setError("Please enter a valid phone number (10–15 digits).");
            setIsLoading(false);
            return;
        }
        const { error } = await supabase.from("feedbacks").insert([
            { name, email, phone, message, rating }
        ]);

        if (error) {
            console.error("Insert error:", error.message);
            alert("Something went wrong!");
        } else {
            setFormData({ name: "", email: "", phone: "", message: "" });
            setRating(5);
        }
        setIsLoading(false);
        setShowPopup(true);
        setCharCount(0);
        setError("");
        // try {
        //     await fetch("https://wtbmmfzbkmyhjwkbykxh.supabase.co/functions/v1/quick-task", {
        //         method: "POST",
        //         headers: {
        //             "Content-Type": "application/json",
        //             apikey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY, // optional if using anon key
        //         },
        //         body: JSON.stringify({ name, email, phone, message, rating }),
        //     });
        //     console.log("Email sent to owner!");
        // } catch (err) {
        //     console.error("Error sending feedback email:", err);
        // }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50 text-gray-800 font-[Poppins] flex flex-col overflow-hidden">
            {/* ANIMATED BACKGROUND ELEMENTS */}
            <div className="fixed inset-0 -z-10 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-red-100 to-transparent rounded-full blur-3xl opacity-60 animate-pulse-slow"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-gray-100 to-transparent rounded-full blur-3xl opacity-60 animate-pulse-slow delay-1000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-red-50 to-gray-50 rounded-full blur-3xl opacity-40 animate-pulse-slow delay-500"></div>
            </div>

            {/* HEADER */}
            <header className="bg-white/80 backdrop-blur-lg shadow-md py-3 px-6 flex justify-center border-b border-gray-100 sticky top-0 z-40">
                <div
                    className={`transform transition-all duration-700 ${isVisible
                        ? "translate-y-0 opacity-100 scale-100"
                        : "-translate-y-8 opacity-0 scale-95"
                        }`}
                >
                    <div className="relative group flex items-center justify-center">
                        {/* Subtle glowing red gradient behind logo */}
                        <div className="absolute -inset-3 bg-gradient-to-r from-red-600 to-red-800 rounded-full blur-md opacity-25 group-hover:opacity-50 transition duration-700 group-hover:duration-200"></div>

                        {/* Logo */}
                        <img
                            src={logo}
                            alt="Toyota Ravi Motors"
                            className="h-16 md:h-20 w-auto relative transform transition-transform duration-300 group-hover:scale-105 filter drop-shadow-lg"
                        />
                    </div>
                </div>
            </header>


            {/* MAIN CONTENT */}
            <main className="flex-1 py-12 px-4 sm:px-8 lg:px-16">
                <div className="grid lg:grid-cols-2 gap-10 max-w-6xl mx-auto">
                    {/* LEFT COLUMN - FORM */}
                    <div className={`transform transition-all duration-700 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-red-600 via-red-500 to-red-700 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-white/50">
                                <div className="flex items-center mb-6">
                                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-700 rounded-lg flex items-center justify-center mr-3 shadow-lg">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </div>
                                    <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                                        Share Your Experience
                                    </h2>
                                </div>

                                <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                                    Tell us about your experience with our service. Your feedback helps us improve and serve you better.
                                </p>

                                {error && (
                                    <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3 rounded-xl mb-6 shadow-lg transform animate-shake">
                                        <div className="flex items-center">
                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            {error}
                                        </div>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="space-y-2">
                                        <label className="block font-semibold text-gray-700 text-sm mb-2">
                                            Full Name *
                                        </label>
                                        <div className="relative group">
                                            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-500 to-red-700 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
                                            <input
                                                type="text"
                                                id="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Enter your full name"
                                                className="relative w-full border border-gray-200 rounded-xl px-4 py-3 bg-white/80 focus:bg-white focus:ring-2 focus:ring-red-500/30 outline-none transition-all duration-300 shadow-sm"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block font-semibold text-gray-700 text-sm mb-2">
                                            Email Address *
                                        </label>
                                        <div className="relative group">
                                            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-500 to-red-700 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
                                            <input
                                                type="email"
                                                id="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="Enter your email address"
                                                className="relative w-full border border-gray-200 rounded-xl px-4 py-3 bg-white/80 focus:bg-white focus:ring-2 focus:ring-red-500/30 outline-none transition-all duration-300 shadow-sm"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block font-semibold text-gray-700 text-sm mb-2">
                                            Phone Number *
                                        </label>
                                        <div className="relative group">
                                            <div className="absolute -inset-0.5 bg-gradient-to-r from-gray-400 to-gray-600 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
                                            <input
                                                type="tel"
                                                id="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="Enter your phone number"
                                                className="relative w-full border border-gray-200 rounded-xl px-4 py-3 bg-white/80 focus:bg-white focus:ring-2 focus:ring-red-500/30 outline-none transition-all duration-300 shadow-sm"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <label className="block font-semibold text-gray-700 text-sm mb-2">
                                            Overall Rating *
                                        </label>
                                        <div className="flex gap-3 justify-center py-2">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <button
                                                    type="button"
                                                    key={star}
                                                    onClick={() => setRating(star)}
                                                    className={`transform transition-all duration-300 hover:scale-125 active:scale-95 ${star <= rating
                                                        ? "text-red-600 scale-110 drop-shadow-lg"
                                                        : "text-gray-300 hover:text-red-400"
                                                        }`}
                                                >
                                                    <svg className="w-10 h-10 filter drop-shadow" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                    </svg>
                                                </button>
                                            ))}

                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block font-semibold text-gray-700 text-sm mb-2">
                                            Your Feedback *
                                        </label>
                                        <div className="relative group">
                                            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-500 to-red-700 rounded-xl blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
                                            <textarea
                                                id="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                maxLength={500}
                                                placeholder="Share your detailed feedback or suggestions..."
                                                className="relative w-full border border-gray-200 rounded-xl px-4 py-3 h-32 resize-none bg-white/80 focus:bg-white focus:ring-2 focus:ring-red-500/30 outline-none transition-all duration-300 shadow-sm"
                                                required
                                            />
                                        </div>
                                        <div className="flex justify-between items-center mt-2">
                                            <span className="text-sm text-gray-500 font-medium">
                                            </span>
                                            <span className={`text-sm font-semibold ${charCount > 450 ? 'text-red-500' : 'text-gray-500'
                                                }`}>
                                                {charCount}/500
                                            </span>
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className={`w-full group relative overflow-hidden bg-gradient-to-r 
        from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 
        text-white py-4 rounded-xl font-bold text-lg shadow-2xl transform 
        transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]
        ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                                    >
                                        {isLoading ? (
                                            <span className="flex items-center justify-center">
                                                <svg
                                                    className="animate-spin h-5 w-5 mr-2 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                    ></path>
                                                </svg>
                                                Submitting...
                                            </span>
                                        ) : (
                                            <span className="relative flex items-center justify-center">
                                                Submit Feedback
                                                <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                                </svg>
                                            </span>
                                        )}
                                    </button>

                                </form>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className={`space-y-8 transform transition-all duration-700 delay-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                        {/* GOOGLE REVIEW CARD */}
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-blue-700 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-white/50 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
                                <div className="text-center">
                                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                                        <img
                                            src={google}   // your google logo
                                            alt="Google Logo"
                                            className="w-16 h-16" // increased size
                                        />
                                    </div>

                                    <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-3">
                                        Leave a Google Review
                                    </h3>
                                    <p className="text-gray-600 mb-6 leading-relaxed">
                                        Help others discover Toyota Ravi Motors by leaving a review on Google.
                                    </p>
                                    <button
                                        onClick={() => window.open("https://www.google.com/search?sca_esv=5fd60c16aeab8c88&rlz=1C1JJTC_enPK1113PK1113&sxsrf=AE3TifMeV6TRP13EApXqS4w-DQC-stOZpA:1762760639689&si=AMgyJEvkVjFQtirYNBhM3ZJIRTaSJ6PxY6y1_6WZHGInbzDnMaJID95s__l3cEa2iGCE6cZD61IFeNO-zzarLmCxEimGjdqGrJrjWQFzwxQE4piX_VbCqJHSoV1cPMSIRBWsVAopnO778Js4--v17aX43PG7OGOtqA%3D%3D&q=Toyota+Ravi+Motors+Reviews&sa=X&ved=2ahUKEwiQyoKOi-eQAxVoT6QEHeL3BgkQ0bkNegQIMBAE&biw=1280&bih=585&dpr=1.25#lrd=0x39190219c5f2b207:0x81dee93f7146875d,3,,,,", "_blank")}
                                        className="group relative overflow-hidden bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-semibold shadow-2xl transform transition-all duration-300 hover:scale-[1.05] active:scale-[0.95]"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                                        <span className="relative flex items-center justify-center">
                                            Write Google Review
                                            <svg
                                                className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                />
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>



                        {/* WHY FEEDBACK MATTERS CARD */}
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-white/50 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
                                <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-6 flex items-center">
                                    <svg className="w-6 h-6 mr-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    Why Your Feedback Matters
                                </h3>
                                <ul className="space-y-4">
                                    {[
                                        { icon: "💡", text: "Helps us improve services and experience" },
                                        { icon: "👥", text: "Assists new customers in making better decisions" },
                                        { icon: "❤️", text: "Motivates our team and recognizes great work" }
                                    ].map((item, index) => (
                                        <li key={index} className="flex items-start group/item">
                                            <span className="text-2xl mr-3 transform group-hover/item:scale-110 transition-transform duration-300">{item.icon}</span>
                                            <span className="text-gray-700 font-medium pt-1">{item.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* SUPPORT CARD */}
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-red-800 rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative bg-gradient-to-br from-red-600 to-red-800 text-white rounded-2xl shadow-2xl p-8 border border-red-500/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 overflow-hidden">
                                <div className="absolute -right-10 -top-10 w-32 h-32 bg-white/10 rounded-full"></div>
                                <div className="absolute -left-10 -bottom-10 w-24 h-24 bg-white/10 rounded-full"></div>

                                <h3 className="text-2xl font-bold mb-4 relative z-10 flex items-center">
                                    <svg className="w-6 h-6 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Need Direct Support?
                                </h3>

                                <p className="mb-6 text-red-100 leading-relaxed relative z-10">
                                    Have urgent concerns or need help? Reach out directly!
                                </p>

                                <div className="space-y-3 relative z-10 text-left">
                                    {/* Email */}
                                    <div className="flex items-center">
                                        <span className="w-8">📧</span>
                                        <span className="font-medium group-hover:text-white transition-colors">customer.relations@toyotaravi.com</span>
                                    </div>

                                    {/* Phone Contacts */}
                                    <div className="flex items-start space-x-2">
                                        <span className="w-8">📞</span>
                                        <div className="space-y-1">
                                            <div>CR: 042-35299482</div>
                                            <div>WHATSAPP: 0316-4788205</div>
                                            <div>Sales: 0321-4357000 / 111-700-900 (Ext: 106, 109)</div>
                                            <div>Parts: 0322-4746473 / 111-700-900 (Ext: 113)</div>
                                            <div>Service: 0301-7830567 / 111-700-900 (Ext: 129)</div>
                                            <div>Body & Paint: 0308-3209604 / 111-700-900 (Ext: 131)</div>
                                            <div>Toyota SURE: 0302-8641111 / 111-700-900 (Ext: 134)</div>
                                        </div>
                                    </div>

                                    {/* Hours */}
                                    <div className="flex items-start space-x-2">
                                        <span className="w-8">⏰</span>
                                        <div className="space-y-1">
                                            <div>Monday - Saturday: 09:00AM - 06:00PM</div>
                                            <div>Sunday: 10:00AM - 05:00PM</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </main>

            {/* SUCCESS POPUP */}
            {showPopup && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-4 animate-fadeIn">
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 text-center max-w-md w-full mx-auto transform animate-scaleIn">
                        <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-3">
                            Thank You!
                        </h2>
                        <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                            Your feedback has been successfully submitted. We appreciate your time!
                        </p>
                    </div>
                </div>
            )}

        </div>
    );
}
